import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Layout } from './CoreComponents';
import BaseGrid from './Shared/BaseGrid';
import ItemQuoteReportModal from './Shared/ItemQuoteReportModal';
import { fmtDate } from './Shared/quoteRoundsView';
import { httpClient } from '../utils/apiCall';
import { API_ENDPOINTS } from '../config/api';
import { type ColDef, type ICellRendererParams } from 'ag-grid-community';

interface ItemRow {
  id: number;
  itemCode: string;
  itemName: string | null;
  unit: string;
  categoryName?: string | null;
  requestCount: number;
  lastRequestDate: string;
}

const ItemQuoteReport: React.FC = () => {
  const [items, setItems] = useState<ItemRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedItemId, setSelectedItemId] = useState<number | null>(null);

  const fetchItems = useCallback(() => {
    setLoading(true);
    setError(null);
    httpClient
      .get<ItemRow[]>(API_ENDPOINTS.ITEM.WITH_PO_REQUESTS)
      .then(r => {
        if (r.success && r.data) {
          setItems(r.data);
        } else {
          setError(r.message || 'Ürünler yüklenemedi');
        }
      })
      .catch(e => setError(e?.message || 'Ürünler yüklenemedi'))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    document.title = 'Ürün Bazlı Teklif Raporu';
    fetchItems();
  }, [fetchItems]);

  const filteredItems = useMemo(() => {
    const term = searchTerm.trim().toLowerCase();
    if (!term) return items;
    return items.filter(
      i =>
        i.itemCode?.toLowerCase().includes(term) ||
        (i.itemName ?? '').toLowerCase().includes(term),
    );
  }, [items, searchTerm]);

  const columnDefs = useMemo<ColDef<ItemRow>[]>(
    () => [
      {
        headerName: 'Ürün Kodu',
        field: 'itemCode',
        flex: 0.6,
        minWidth: 130,
        cellRenderer: ({ data }: ICellRendererParams<ItemRow>) => (
          <span className="font-medium text-gray-900 dark:text-white">{data?.itemCode}</span>
        ),
      },
      {
        headerName: 'Ürün Adı',
        field: 'itemName',
        flex: 1.6,
        minWidth: 220,
        valueGetter: p => p.data?.itemName ?? '',
        cellRenderer: ({ data }: ICellRendererParams<ItemRow>) => (
          <span className="text-gray-900 dark:text-white" title={data?.itemName ?? undefined}>
            {data?.itemName ?? '—'}
          </span>
        ),
      },
      {
        headerName: 'Birim',
        field: 'unit',
        flex: 0.3,
        minWidth: 80,
      },
      {
        headerName: 'Kategori',
        field: 'categoryName',
        flex: 0.5,
        minWidth: 140,
        valueGetter: p => p.data?.categoryName ?? '',
      },
      {
        headerName: 'Talep Sayısı',
        field: 'requestCount',
        flex: 0.4,
        minWidth: 110,
        type: 'rightAligned',
      },
      {
        headerName: 'Son Talep',
        field: 'lastRequestDate',
        flex: 0.5,
        minWidth: 130,
        valueFormatter: p => fmtDate(p.value, false),
        sort: 'desc',
      },
      {
        headerName: 'Rapor',
        colId: 'report',
        flex: 0.3,
        minWidth: 100,
        sortable: false,
        filter: false,
        cellStyle: { display: 'flex', alignItems: 'center' },
        cellRenderer: ({ data }: ICellRendererParams<ItemRow>) => (
          <button
            type="button"
            onClick={() => data && setSelectedItemId(data.id)}
            className="px-2 py-1 text-[11px] font-medium border rounded transition-colors whitespace-nowrap text-blue-600 dark:text-blue-400 hover:text-blue-800 dark:hover:text-blue-300 border-blue-300 dark:border-blue-700 hover:bg-blue-50 dark:hover:bg-blue-900/20"
          >
            <i className="fas fa-chart-bar mr-1 text-[10px]" />
            Görüntüle
          </button>
        ),
      },
    ],
    [],
  );

  return (
    <Layout currentScreen="item-quote-report">
      <div className="max-w-8xl mx-auto h-full flex flex-col">
        {/* Üst bar */}
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 p-4 mb-4 transition-colors duration-300 flex-shrink-0">
          <div className="flex flex-wrap items-end gap-3">
            <div className="flex-1 min-w-[300px]">
              <input
                type="text"
                placeholder="Ürün kodu veya adı ile ara..."
                value={searchTerm}
                onChange={e => setSearchTerm(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-white placeholder-gray-500 text-sm"
              />
            </div>
            <button
              type="button"
              onClick={() => {
                setSearchTerm('');
                fetchItems();
              }}
              disabled={loading}
              className="px-3 py-2 bg-primary text-white rounded-lg hover:bg-blue-700 transition-colors text-sm h-[38px] flex items-center justify-center min-w-[100px] disabled:opacity-60 disabled:cursor-not-allowed"
            >
              <i className={`fas fa-sync-alt mr-2 ${loading ? 'fa-spin' : ''}`} />
              Yenile
            </button>
          </div>
        </div>

        {/* Tablo */}
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 transition-colors duration-300 flex flex-col flex-1 min-h-0 overflow-hidden relative">
          {loading && items.length === 0 && (
            <div className="absolute inset-0 z-10 flex items-center justify-center bg-white/80 dark:bg-gray-800/80">
              <div className="flex flex-col items-center gap-2 text-gray-500 dark:text-gray-400">
                <i className="fas fa-spinner fa-spin text-2xl" />
                <span className="text-sm">Yükleniyor...</span>
              </div>
            </div>
          )}
          {error && (
            <div className="absolute inset-0 z-10 flex items-center justify-center bg-white/90 dark:bg-gray-800/90">
              <div className="text-sm text-red-600 dark:text-red-400">{error}</div>
            </div>
          )}

          <div className="flex flex-col flex-1 min-h-0">
            <BaseGrid<ItemRow>
              gridStateKey="item-quote-report-grid"
              rowData={filteredItems}
              columnDefs={columnDefs}
              defaultColDef={{
                sortable: true,
                filter: true,
                resizable: true,
                floatingFilter: false,
              }}
              rowHeight={40}
              domLayout="normal"
              localeText={{
                noRowsToShow: searchTerm.trim()
                  ? 'Arama kriterlerinize uygun ürün bulunamadı.'
                  : 'Talep oluşturulmuş ürün bulunmuyor.',
              }}
            />
          </div>
        </div>
      </div>

      {selectedItemId !== null && (
        <ItemQuoteReportModal itemId={selectedItemId} onClose={() => setSelectedItemId(null)} />
      )}
    </Layout>
  );
};

export default ItemQuoteReport;
